import { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import {
  getSuccessStories, updateSuccessStory, deleteSuccessStory,
  getSuccessStory,
  getRegions, getCities, uploadProjectImage, getImageUrl,
  aiStoryHook, aiStoryImproveChallenge, aiStoryResultsSummary, aiStorySupportTags,
  aiGenerateStoryFromProject,
  searchProjectsLight, seedDemoStories,
} from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Pencil, Trash2, Loader2, Search, ChevronLeft, ChevronRight, Sparkles, Image, X, Award, Plus, Check, Database } from 'lucide-react';
import { toast } from 'sonner';

const ALLOWED_SUPPORT_TYPES = ['تمويل', 'تدريب', 'استشارات', 'جادة 30', 'منافذ بيع', 'برامج'];
const JOURNEY_TAGS = ['قبل', 'أثناء', 'بعد'];
const MAX_HOOK = 80;
const MAX_CHALLENGE = 200;
const MAX_METRICS = 4;
const MAX_IMAGES = 4;

const SECTORS = [
  'الصناعة والتصنيع', 'الزراعة والثروة الحيوانية', 'التجارة والتجزئة',
  'السياحة والضيافة', 'التقنية والاتصالات', 'الخدمات المالية',
  'الصحة والرعاية الطبية', 'التعليم والتدريب', 'أخرى',
];

const EMPTY_FORM = {
  title_ar: '', title_en: '', city: '', region: '', sector: '',
  latitude: '', longitude: '', short_hook: '', challenge_text: '',
  support_types: [], result_metrics: [],
  cover_image: '', gallery_images: [], gallery_tags: [],
  related_project_id: '',
  is_active: true, display_priority: 0,
};

export default function SuccessStoriesPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState({ items: [], total: 0 });
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [page, setPage] = useState(0);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [aiLoading, setAiLoading] = useState({});
  const [section, setSection] = useState('basic');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectResults, setProjectResults] = useState([]);
  const [projectSearching, setProjectSearching] = useState(false);
  const [selectedProjectLabel, setSelectedProjectLabel] = useState('');
  const [errors, setErrors] = useState({});
  const pageSize = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [storiesRes, regionsRes, citiesRes] = await Promise.all([
        getSuccessStories({ skip: page * pageSize, limit: pageSize, search: searchTerm || undefined }),
        getRegions(),
        getCities(),
      ]);
      setStories(storiesRes.data);
      setRegions(regionsRes.data);
      setCities(citiesRes.data);
    } catch { toast.error(isAr ? 'خطأ في تحميل البيانات' : 'Error loading data'); }
    setLoading(false);
  }, [page, searchTerm, isAr]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && !dialogOpen) {
      getSuccessStory(editId).then(res => {
        openEdit(res.data);
        setSearchParams({}, { replace: true });
      }).catch(() => {});
    }
  }, [searchParams]); // eslint-disable-line

  const openEdit = async (s) => {
    setEditing(s);
    const metrics = s.result_metrics || [];
    setForm({
      ...EMPTY_FORM,
      ...s,
      latitude: s.latitude ?? '',
      longitude: s.longitude ?? '',
      support_types: (s.support_types || []).filter(t => ALLOWED_SUPPORT_TYPES.includes(t)),
      result_metrics: Array.isArray(metrics) ? metrics : [],
      gallery_images: (s.gallery_images || []).slice(0, MAX_IMAGES),
      gallery_tags: s.gallery_tags || [],
      related_project_id: s.related_project_id || '',
    });
    setProjectResults([]); setProjectSearch(''); setErrors({});
    if (s.related_project_id) {
      try {
        const { getProject } = await import('../lib/api');
        const res = await getProject(s.related_project_id);
        setSelectedProjectLabel(res.data.project_name_ar || s.related_project_id);
      } catch { setSelectedProjectLabel(s.related_project_id); }
    } else {
      setSelectedProjectLabel('');
    }
    setSection('basic');
    setDialogOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.title_ar.trim()) e.title_ar = isAr ? 'العنوان مطلوب' : 'Title required';
    if (!form.short_hook.trim()) e.short_hook = isAr ? 'العنوان الجذاب مطلوب' : 'Hook required';
    if (form.short_hook.length > MAX_HOOK) e.short_hook = isAr ? `الحد الأقصى ${MAX_HOOK} حرف` : `Max ${MAX_HOOK} chars`;
    if (form.challenge_text.length > MAX_CHALLENGE) e.challenge_text = isAr ? `الحد الأقصى ${MAX_CHALLENGE} حرف` : `Max ${MAX_CHALLENGE} chars`;
    if (!form.related_project_id) e.related_project_id = isAr ? 'ربط المشروع مطلوب' : 'Project link required';
    if (form.result_metrics.length > MAX_METRICS) e.result_metrics = isAr ? `الحد الأقصى ${MAX_METRICS} نتائج` : `Max ${MAX_METRICS} items`;
    if (form.gallery_images.length > MAX_IMAGES) e.gallery_images = isAr ? `الحد الأقصى ${MAX_IMAGES} صور` : `Max ${MAX_IMAGES} images`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      const firstErr = Object.keys(errors)[0];
      const sectionMap = { title_ar: 'basic', short_hook: 'story', challenge_text: 'story', related_project_id: 'project', result_metrics: 'results', gallery_images: 'images' };
      if (sectionMap[firstErr]) setSection(sectionMap[firstErr]);
      toast.error(isAr ? 'يرجى تصحيح الأخطاء' : 'Please fix errors');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        display_priority: parseInt(form.display_priority) || 0,
      };
      if (editing) {
        await updateSuccessStory(editing.id, payload);
        toast.success(isAr ? 'تم التحديث بنجاح' : 'Updated successfully');
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'حدث خطأ' : 'Error'));
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteSuccessStory(deleting.id);
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      setDeleteDialogOpen(false);
      loadData();
    } catch { toast.error(isAr ? 'فشل الحذف' : 'Delete failed'); }
  };

  const handleSeedStories = async () => {
    setSeeding(true);
    try {
      const res = await seedDemoStories();
      toast.success(isAr ? `تم إنشاء ${res.data.count} قصة نجاح تجريبية` : `Seeded ${res.data.count} demo stories`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل إنشاء البيانات' : 'Seed failed'));
    }
    setSeeding(false);
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (field === 'gallery' && form.gallery_images.length >= MAX_IMAGES) {
      toast.error(isAr ? `الحد الأقصى ${MAX_IMAGES} صور` : `Max ${MAX_IMAGES} images`);
      return;
    }
    try {
      const res = await uploadProjectImage(file);
      if (field === 'cover_image') {
        setForm(f => ({ ...f, cover_image: res.data.image_path }));
      } else {
        setForm(f => ({
          ...f,
          gallery_images: [...(f.gallery_images || []), res.data.image_path],
          gallery_tags: [...(f.gallery_tags || []), ''],
        }));
      }
      toast.success(isAr ? 'تم رفع الصورة' : 'Image uploaded');
    } catch { toast.error(isAr ? 'فشل رفع الصورة' : 'Upload failed'); }
  };

  const removeGalleryImage = (idx) => {
    setForm(f => ({
      ...f,
      gallery_images: f.gallery_images.filter((_, i) => i !== idx),
      gallery_tags: (f.gallery_tags || []).filter((_, i) => i !== idx),
    }));
  };

  const setGalleryTag = (idx, tag) => {
    setForm(f => {
      const tags = [...(f.gallery_tags || [])];
      while (tags.length <= idx) tags.push('');
      tags[idx] = tag;
      return { ...f, gallery_tags: tags };
    });
  };

  /* ── AI Helpers ── */
  const runAI = async (key, apiFn, payload) => {
    setAiLoading(p => ({ ...p, [key]: true }));
    try {
      const res = await apiFn(payload);
      return res.data.result !== undefined ? res.data.result : res.data;
    } catch { toast.error(isAr ? 'فشل الذكاء الاصطناعي' : 'AI failed'); return null; }
    finally { setAiLoading(p => ({ ...p, [key]: false })); }
  };

  const genHook = async () => {
    const r = await runAI('hook', aiStoryHook, { title: form.title_ar, challenge: form.challenge_text, sector: form.sector });
    if (r) setForm(f => ({ ...f, short_hook: String(r).slice(0, MAX_HOOK) }));
  };
  const improveChallenge = async () => {
    const r = await runAI('challenge', aiStoryImproveChallenge, { text: form.challenge_text });
    if (r) setForm(f => ({ ...f, challenge_text: String(r).slice(0, MAX_CHALLENGE) }));
  };
  const genResults = async () => {
    const r = await runAI('results', aiStoryResultsSummary, { title: form.title_ar, existing: form.result_metrics });
    if (r && Array.isArray(r)) setForm(f => ({ ...f, result_metrics: r.slice(0, MAX_METRICS) }));
  };
  const suggestTags = async () => {
    const r = await runAI('tags', aiStorySupportTags, { title: form.title_ar, challenge: form.challenge_text });
    if (r && Array.isArray(r)) setForm(f => ({ ...f, support_types: r.filter(t => ALLOWED_SUPPORT_TYPES.includes(t)) }));
  };

  const genFullStory = async () => {
    setAiLoading(p => ({ ...p, fullStory: true }));
    try {
      const res = await aiGenerateStoryFromProject({ title: form.title_ar, sector: form.sector, city: form.city });
      const d = res.data;
      setForm(f => ({
        ...f,
        short_hook: (d.short_hook || '').slice(0, MAX_HOOK),
        challenge_text: (d.challenge_text || '').slice(0, MAX_CHALLENGE),
        support_types: (d.support_types || []).filter(t => ALLOWED_SUPPORT_TYPES.includes(t)),
        result_metrics: Array.isArray(d.result_metrics) ? d.result_metrics.slice(0, MAX_METRICS) : f.result_metrics,
      }));
      toast.success(isAr ? 'تم توليد القصة — راجع البيانات وعدّل حسب الحاجة' : 'Story generated — review and edit');
    } catch { toast.error(isAr ? 'فشل التوليد' : 'Generation failed'); }
    setAiLoading(p => ({ ...p, fullStory: false }));
  };

  /* ── Project search ── */
  const handleProjectSearch = async (q) => {
    setProjectSearch(q);
    if (q.length < 2) { setProjectResults([]); return; }
    setProjectSearching(true);
    try {
      const res = await searchProjectsLight(q);
      setProjectResults(res.data || []);
    } catch { setProjectResults([]); }
    setProjectSearching(false);
  };

  const selectProject = (p) => {
    setForm(f => ({ ...f, related_project_id: p.id }));
    setSelectedProjectLabel(`${p.project_name_ar}${p.city_name ? ' - ' + p.city_name : ''}`);
    setProjectResults([]); setProjectSearch('');
    setErrors(e => { const { related_project_id, ...rest } = e; return rest; });
  };

  /* ── Metric helpers ── */
  const addMetric = () => {
    if (form.result_metrics.length >= MAX_METRICS) return;
    setForm(f => ({ ...f, result_metrics: [...f.result_metrics, { label: '', value: '', highlight: false }] }));
  };
  const updateMetric = (idx, field, val) => {
    setForm(f => {
      const m = [...f.result_metrics];
      m[idx] = { ...m[idx], [field]: val };
      return { ...f, result_metrics: m };
    });
  };
  const removeMetric = (idx) => {
    setForm(f => ({ ...f, result_metrics: f.result_metrics.filter((_, i) => i !== idx) }));
  };

  const AIBtn = ({ onClick, loading: ld, label }) => (
    <Button type="button" variant="outline" size="sm" onClick={onClick} disabled={ld}
      className="gap-1.5 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
      data-testid={`ai-btn-${label.replace(/\s/g, '-')}`}>
      {ld ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {label}
    </Button>
  );

  const CharCounter = ({ current, max }) => (
    <span className={`text-[10px] tabular-nums ${current > max ? 'text-red-500 font-bold' : current > max * 0.8 ? 'text-amber-500' : 'text-gray-400'}`}>
      {current}/{max}
    </span>
  );

  const FieldError = ({ name }) => errors[name] ? <p className="text-xs text-red-500 mt-0.5">{errors[name]}</p> : null;

  const tabs = [
    { id: 'basic', label: isAr ? 'المعلومات الأساسية' : 'Basic Info', num: '1' },
    { id: 'story', label: isAr ? 'محتوى القصة' : 'Story Content', num: '2' },
    { id: 'results', label: isAr ? 'الدعم والنتائج' : 'Support & Results', num: '3' },
    { id: 'images', label: isAr ? 'الصور' : 'Images', num: '4' },
    { id: 'project', label: isAr ? 'ربط المشروع' : 'Project Link', num: '5' },
  ];

  const totalPages = Math.ceil(stories.total / pageSize);

  const toggleSupport = (type) => {
    setForm(f => {
      const has = f.support_types.includes(type);
      return { ...f, support_types: has ? f.support_types.filter(t => t !== type) : [...f.support_types, type] };
    });
  };

  return (
    <div className="space-y-4" data-testid="success-stories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
            <Award className="w-5 h-5 text-amber-500" />
            {isAr ? 'قصص النجاح' : 'Success Stories'}
          </h2>
          <p className="text-sm text-gray-500">{isAr ? `إجمالي: ${stories.total}` : `Total: ${stories.total}`}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder={isAr ? 'بحث...' : 'Search...'} value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
              className="pr-9 w-56" data-testid="search-input" />
          </div>
          <Button variant="outline" size="sm" onClick={handleSeedStories} disabled={seeding}
            className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="seed-stories-btn">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {isAr ? 'إنشاء 20 قصة تجريبية' : 'Seed 20 Demo Stories'}
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : stories.items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">{isAr ? 'لا توجد قصص نجاح' : 'No success stories'}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isAr ? 'العنوان' : 'Title'}</TableHead>
                  <TableHead>{isAr ? 'العنوان الجذاب' : 'Hook'}</TableHead>
                  <TableHead>{isAr ? 'المدينة' : 'City'}</TableHead>
                  <TableHead>{isAr ? 'القطاع' : 'Sector'}</TableHead>
                  <TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stories.items.map(s => (
                  <TableRow key={s.id} data-testid={`story-row-${s.id}`}>
                    <TableCell className="font-medium max-w-[200px] truncate">{s.title_ar}</TableCell>
                    <TableCell className="max-w-[180px] truncate text-gray-500 text-xs">{s.short_hook || '-'}</TableCell>
                    <TableCell>{s.city || '-'}</TableCell>
                    <TableCell className="text-xs">{s.sector || '-'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {s.is_active ? (isAr ? 'نشطة' : 'Active') : (isAr ? 'معطلة' : 'Inactive')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setDeleting(s); setDeleteDialogOpen(true); }} data-testid={`delete-${s.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
            <ChevronLeft className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="story-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              {isAr ? 'تعديل قصة نجاح' : 'Edit Story'}
            </DialogTitle>
          </DialogHeader>

          {/* Section Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg overflow-x-auto" data-testid="form-tabs">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setSection(t.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  section === t.id ? 'bg-white text-[#0A4C3A] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid={`tab-${t.id}`}>
                <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center font-bold ${section === t.id ? 'bg-[#0A4C3A] text-white' : 'bg-gray-300 text-white'}`}>{t.num}</span>
                {t.label}
              </button>
            ))}
          </div>

          {/* AI Full Story Button */}
          <Button type="button" variant="outline" onClick={genFullStory}
            disabled={aiLoading.fullStory || !form.title_ar}
            className="w-full gap-2 border-amber-400/40 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400 font-bold"
            data-testid="ai-generate-full-story">
            {aiLoading.fullStory ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {isAr ? 'توليد قصة كاملة بالذكاء الاصطناعي' : 'AI Generate Full Story'}
          </Button>

          <div className="space-y-4 mt-1">
            {/* ═══ 1. BASIC INFO ═══ */}
            {section === 'basic' && (
              <div className="space-y-4" data-testid="section-basic">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{isAr ? 'العنوان بالعربية *' : 'Title (Arabic) *'}</Label>
                    <Input value={form.title_ar} onChange={e => setForm(f => ({ ...f, title_ar: e.target.value }))}
                      data-testid="input-title-ar" />
                    <FieldError name="title_ar" />
                  </div>
                  <div>
                    <Label>{isAr ? 'العنوان بالإنجليزية' : 'Title (English)'}</Label>
                    <Input value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))}
                      data-testid="input-title-en" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>{isAr ? 'المنطقة' : 'Region'}</Label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm bg-white" value={form.region}
                      onChange={e => setForm(f => ({ ...f, region: e.target.value }))} data-testid="select-region">
                      <option value="">{isAr ? 'اختر...' : 'Select...'}</option>
                      {regions.map(r => <option key={r.id} value={r.name_ar}>{r.name_ar}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>{isAr ? 'المدينة' : 'City'}</Label>
                    <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                      data-testid="input-city" />
                  </div>
                  <div>
                    <Label>{isAr ? 'القطاع' : 'Sector'}</Label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm bg-white" value={form.sector}
                      onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} data-testid="select-sector">
                      <option value="">{isAr ? 'اختر...' : 'Select...'}</option>
                      {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isAr ? 'خط العرض' : 'Latitude'}</Label>
                    <Input type="number" step="any" value={form.latitude}
                      onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} data-testid="input-lat" />
                  </div>
                  <div>
                    <Label>{isAr ? 'خط الطول' : 'Longitude'}</Label>
                    <Input type="number" step="any" value={form.longitude}
                      onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} data-testid="input-lng" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>{isAr ? 'نشطة' : 'Active'}</Label>
                    <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                      data-testid="switch-active" />
                  </div>
                  <div className="flex-1">
                    <Label>{isAr ? 'أولوية العرض' : 'Display Priority'}</Label>
                    <Input type="number" value={form.display_priority}
                      onChange={e => setForm(f => ({ ...f, display_priority: e.target.value }))} data-testid="input-priority" />
                  </div>
                </div>
              </div>
            )}

            {/* ═══ 2. STORY CONTENT ═══ */}
            {section === 'story' && (
              <div className="space-y-5" data-testid="section-story">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-2">
                      {isAr ? 'العنوان الجذاب *' : 'Short Hook *'}
                      <CharCounter current={form.short_hook.length} max={MAX_HOOK} />
                    </Label>
                    <AIBtn onClick={genHook} loading={aiLoading.hook} label={isAr ? 'توليد' : 'Generate'} />
                  </div>
                  <Input value={form.short_hook}
                    onChange={e => {
                      const v = e.target.value.replace(/\n/g, '');
                      if (v.length <= MAX_HOOK) setForm(f => ({ ...f, short_hook: v }));
                    }}
                    placeholder={isAr ? 'سطر واحد ملهم وقصير...' : 'One short inspiring line...'}
                    maxLength={MAX_HOOK}
                    data-testid="input-hook" />
                  <FieldError name="short_hook" />
                  <p className="text-[10px] text-gray-400 mt-0.5">{isAr ? 'سطر واحد فقط — لا فقرات' : 'Single line only — no paragraphs'}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-2">
                      {isAr ? 'نص التحدي' : 'Challenge Text'}
                      <CharCounter current={form.challenge_text.length} max={MAX_CHALLENGE} />
                    </Label>
                    <AIBtn onClick={improveChallenge} loading={aiLoading.challenge} label={isAr ? 'تحسين' : 'Improve'} />
                  </div>
                  <Textarea value={form.challenge_text}
                    onChange={e => {
                      const v = e.target.value;
                      if (v.length <= MAX_CHALLENGE) setForm(f => ({ ...f, challenge_text: v }));
                    }}
                    placeholder={isAr ? 'وصف مختصر للتحدي (سطرين كحد أقصى)...' : 'Brief challenge description (2 lines max)...'}
                    rows={2} maxLength={MAX_CHALLENGE}
                    data-testid="input-challenge" />
                  <FieldError name="challenge_text" />
                  <p className="text-[10px] text-gray-400 mt-0.5">{isAr ? 'سطرين كحد أقصى — بدون فقرات طويلة' : 'Max 2 lines — no long paragraphs'}</p>
                </div>
              </div>
            )}

            {/* ═══ 3. SUPPORT & RESULTS ═══ */}
            {section === 'results' && (
              <div className="space-y-5" data-testid="section-results">
                {/* Support Types - Multi-select */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>{isAr ? 'أنواع الدعم' : 'Support Types'}</Label>
                    <AIBtn onClick={suggestTags} loading={aiLoading.tags} label={isAr ? 'اقتراح' : 'Suggest'} />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="support-types-grid">
                    {ALLOWED_SUPPORT_TYPES.map(type => {
                      const active = form.support_types.includes(type);
                      return (
                        <button key={type} type="button" onClick={() => toggleSupport(type)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                            active
                              ? 'border-amber-400 bg-amber-50 text-amber-800 font-medium'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                          }`} data-testid={`support-${type}`}>
                          <span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                            {active && <Check className="w-3 h-3 text-white" />}
                          </span>
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Result Metrics - Structured */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      {isAr ? 'النتائج' : 'Results'}
                      <span className="text-[10px] text-gray-400">({form.result_metrics.length}/{MAX_METRICS})</span>
                    </Label>
                    <AIBtn onClick={genResults} loading={aiLoading.results} label={isAr ? 'اقتراح نتائج' : 'Suggest'} />
                  </div>
                  <div className="space-y-2" data-testid="metrics-list">
                    {form.result_metrics.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50" data-testid={`metric-${i}`}>
                        <Input value={m.value} placeholder={isAr ? '+40' : '+40'}
                          onChange={e => updateMetric(i, 'value', e.target.value)}
                          className="w-20 text-center font-bold text-lg" data-testid={`metric-value-${i}`} />
                        <Input value={m.label} placeholder={isAr ? 'وصف قصير' : 'Short label'}
                          onChange={e => updateMetric(i, 'label', e.target.value)}
                          className="flex-1" data-testid={`metric-label-${i}`} />
                        <button type="button" onClick={() => updateMetric(i, 'highlight', !m.highlight)}
                          className={`px-2 py-1 rounded text-[10px] font-medium border ${m.highlight ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-white border-gray-200 text-gray-400'}`}
                          data-testid={`metric-highlight-${i}`}>
                          {isAr ? 'مميز' : 'Featured'}
                        </button>
                        <button type="button" onClick={() => removeMetric(i)} className="text-red-400 hover:text-red-600"
                          data-testid={`metric-remove-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {form.result_metrics.length < MAX_METRICS && (
                    <Button type="button" variant="outline" size="sm" onClick={addMetric}
                      className="mt-2 gap-1 text-xs w-full border-dashed" data-testid="add-metric-btn">
                      <Plus className="w-3 h-3" /> {isAr ? 'إضافة نتيجة' : 'Add Metric'}
                    </Button>
                  )}
                  <FieldError name="result_metrics" />
                  <p className="text-[10px] text-gray-400 mt-1">{isAr ? 'مثال: +40 مشروع، +3 مليون مبيعات — أرقام قصيرة فقط' : 'Example: +40 projects, +3M sales — short numbers only'}</p>
                </div>
              </div>
            )}

            {/* ═══ 4. IMAGES ═══ */}
            {section === 'images' && (
              <div className="space-y-5" data-testid="section-images">
                {/* Cover Image */}
                <div>
                  <Label>{isAr ? 'صورة الغلاف *' : 'Cover Image *'}</Label>
                  {form.cover_image ? (
                    <div className="relative w-full h-40 rounded-lg overflow-hidden border mt-1">
                      <img src={getImageUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                      <button className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1"
                        onClick={() => setForm(f => ({ ...f, cover_image: '' }))} data-testid="remove-cover">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 mt-1" data-testid="upload-cover">
                      <Image className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">{isAr ? 'اختر صورة الغلاف' : 'Choose cover image'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, 'cover_image')} />
                    </label>
                  )}
                </div>

                {/* Journey Images */}
                <div>
                  <Label className="flex items-center gap-2">
                    {isAr ? 'صور الرحلة' : 'Journey Images'}
                    <span className="text-[10px] text-gray-400">({(form.gallery_images || []).length}/{MAX_IMAGES})</span>
                  </Label>
                  <div className="space-y-2 mt-2">
                    {(form.gallery_images || []).map((img, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 border rounded-lg bg-gray-50" data-testid={`journey-img-${i}`}>
                        <div className="w-16 h-16 rounded-lg overflow-hidden border flex-shrink-0">
                          <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                        </div>
                        <select value={(form.gallery_tags || [])[i] || ''}
                          onChange={e => setGalleryTag(i, e.target.value)}
                          className="border rounded-md px-2 py-1.5 text-sm bg-white min-w-[80px]"
                          data-testid={`journey-tag-${i}`}>
                          <option value="">{isAr ? 'بدون وسم' : 'No tag'}</option>
                          {JOURNEY_TAGS.map(tag => <option key={tag} value={tag}>{tag}</option>)}
                        </select>
                        <div className="flex-1" />
                        <button onClick={() => removeGalleryImage(i)} className="text-red-400 hover:text-red-600"
                          data-testid={`journey-remove-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {(form.gallery_images || []).length < MAX_IMAGES && (
                    <label className="flex items-center justify-center w-full h-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 mt-2 gap-2 text-xs text-gray-500" data-testid="upload-gallery">
                      <Plus className="w-4 h-4 text-gray-400" />
                      {isAr ? 'إضافة صورة' : 'Add Image'}
                      <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, 'gallery')} />
                    </label>
                  )}
                  <FieldError name="gallery_images" />
                  <p className="text-[10px] text-gray-400 mt-1">{isAr ? 'حد أقصى 4 صور — يمكن وسم كل صورة: قبل، أثناء، بعد' : 'Max 4 images — tag each: before, during, after'}</p>
                </div>
              </div>
            )}

            {/* ═══ 5. PROJECT LINK ═══ */}
            {section === 'project' && (
              <div className="space-y-4" data-testid="section-project">
                <div>
                  <Label>{isAr ? 'المشروع المرتبط *' : 'Related Project *'}</Label>
                  {form.related_project_id ? (
                    <div className="flex items-center gap-2 mt-1 p-3 border rounded-lg bg-emerald-50 border-emerald-200">
                      <Award className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span className="flex-1 text-sm text-emerald-800 font-medium">{selectedProjectLabel || form.related_project_id}</span>
                      <button onClick={() => { setForm(f => ({ ...f, related_project_id: '' })); setSelectedProjectLabel(''); }}
                        className="text-red-400 hover:text-red-600" data-testid="clear-project">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative mt-1">
                      <Input
                        value={projectSearch}
                        onChange={e => handleProjectSearch(e.target.value)}
                        placeholder={isAr ? 'ابحث عن مشروع بالاسم...' : 'Search project by name...'}
                        data-testid="input-project-search"
                      />
                      {projectSearching && <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
                      {projectResults.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-md shadow-lg" data-testid="project-search-results">
                          {projectResults.map(p => (
                            <button key={p.id} onClick={() => selectProject(p)}
                              className="w-full text-right px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-2">
                              <span className="font-medium text-gray-800">{p.project_name_ar}</span>
                              {p.city_name && <span className="text-xs text-gray-400">- {p.city_name}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <FieldError name="related_project_id" />
                  <p className="text-xs text-red-400 mt-1 font-medium">{isAr ? 'مطلوب — لا يمكن حفظ القصة بدون ربطها بمشروع' : 'Required — cannot save without linking to a project'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-btn">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-btn"
              className="bg-[#0A4C3A] hover:bg-[#073B2D]">
              {saving && <Loader2 className="w-4 h-4 animate-spin me-1" />}
              {isAr ? 'تحديث' : 'Update'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isAr ? 'تأكيد الحذف' : 'Confirm Delete'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isAr ? `هل أنت متأكد من حذف "${deleting?.title_ar}"؟` : `Delete "${deleting?.title_ar}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">{isAr ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">
              {isAr ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
